import { BookOpen, Sparkles } from 'lucide-react'
import { useLocale } from '../i18n/context'
import type { FillAllPreflightResponse } from '../hooks/useFillAll'

interface Props {
  preflight: FillAllPreflightResponse | null
  loading: boolean
  error: string | null
  starting: boolean
  onConfirm: () => void
  onCancel: () => void
  /** Shown when the split resolves to zero cards. Defaults to the deck-wide
   * wording; the multiselect bar's Fill (a selection, not necessarily the
   * whole deck) passes its own — "this deck" would be wrong for a subset. */
  nothingToFillMessage?: string
  /**
   * Where the panel sits: docked to the bottom edge (the phone's sheet,
   * default) or centred as a dialog (desktop).
   *
   * A placement, not a second component. The disclosure itself — the split, the
   * bar, the allowance line, the two buttons — is identical on both surfaces
   * and must stay that way; what differs is that a full-width panel welded to
   * the bottom edge of a 1440 px window is a phone gesture with nothing to
   * answer it. Everything below this prop is shared verbatim.
   */
  layout?: 'sheet' | 'dialog'
}

// Fill-all confirmation sheet (Prompt 8, frame "Fill all — confirmation").
// Reused as-is by the multiselect bar's Fill (brief §23 item 11 / §25 item 7)
// — same shape, computed over a selection instead of the whole deck. The
// split is shown *before* anything runs — this component never starts
// anything on its own; onConfirm is the only thing that does.
export default function FillAllSheet({ preflight, loading, error, starting, onConfirm, onCancel, nothingToFillMessage, layout = 'sheet' }: Props) {
  const { tr } = useLocale()
  const dm = tr.deckManagement

  const dictCount = preflight?.dictionary_count ?? 0
  const aiCount = preflight?.ai_count ?? 0
  const total = dictCount + aiCount
  const dictPct = total > 0 ? (dictCount / total) * 100 : 0
  const dialog = layout === 'dialog'

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(45,52,54,0.32)', display: 'flex', alignItems: dialog ? 'center' : 'flex-end', justifyContent: 'center', zIndex: 150, padding: dialog ? 16 : 0 }}>
      <div onClick={onCancel} style={{ position: 'absolute', inset: 0 }} />
      <div style={{
        position: 'relative',
        width: '100%',
        maxWidth: dialog ? 420 : undefined,
        background: 'var(--bg-surface)',
        borderRadius: dialog ? 'var(--radius-md)' : '24px 24px 0 0',
        boxShadow: 'var(--shadow-float)',
        padding: dialog ? '22px 0' : '8px 0 max(22px, env(safe-area-inset-bottom, 0px))',
      }}>
        {/* The grab handle is a gesture affordance for a docked sheet, and
            there is no gesture to afford in a centred dialog. */}
        {!dialog && <div style={{ width: 38, height: 4, borderRadius: 'var(--radius-pill)', background: 'var(--border)', margin: '6px auto 14px' }} />}
        <div style={{ padding: '0 20px', display: 'flex', flexDirection: 'column', gap: 14 }}>
          {loading ? (
            <div style={{ padding: '24px 0', textAlign: 'center', fontFamily: 'var(--font-ui)', fontSize: 13.5, color: 'var(--text-secondary)' }}>
              {dm.fillAllPreflightLoading}
            </div>
          ) : error ? (
            <>
              <div style={{ fontFamily: 'var(--font-ui)', fontSize: 13.5, color: 'var(--danger)' }}>{error}</div>
              <button onClick={onCancel} style={cancelBtnStyle}>{tr.deckDetail.cancelLabel}</button>
            </>
          ) : preflight && preflight.total_unfilled === 0 ? (
            <>
              <div style={{ fontFamily: 'var(--font-ui)', fontSize: 13.5, color: 'var(--text-secondary)' }}>{nothingToFillMessage ?? dm.fillAllNothingToFill}</div>
              <button onClick={onCancel} style={cancelBtnStyle}>{tr.deckDetail.cancelLabel}</button>
            </>
          ) : preflight ? (
            <>
              <div>
                <div style={{ fontFamily: 'var(--font-ui)', fontWeight: 700, fontSize: 17, color: 'var(--text-primary)' }}>
                  {dm.fillAllTitle(preflight.total_unfilled)}
                </div>
                <div style={{ fontFamily: 'var(--font-ui)', fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.5, marginTop: 4 }}>
                  {dm.fillAllSubtitle}
                </div>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
                {dictCount > 0 && (
                  <div style={statRowStyle}>
                    <span style={{ ...statIconStyle, background: 'var(--success-soft)', color: 'var(--success)' }}>
                      <BookOpen size={16} />
                    </span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontFamily: 'var(--font-ui)', fontWeight: 600, fontSize: 14, color: 'var(--text-primary)' }}>
                        {dm.fillAllDictionaryRow(dictCount)}
                      </div>
                      <div style={{ fontFamily: 'var(--font-ui)', fontSize: 11.5, color: 'var(--text-secondary)' }}>{dm.fillAllDictionaryNote}</div>
                    </div>
                    <span style={{ fontFamily: 'var(--font-content)', fontWeight: 700, fontSize: 18, color: 'var(--success)' }}>{dictCount}</span>
                  </div>
                )}
                {aiCount > 0 && (
                  <div style={statRowStyle}>
                    <span style={{ ...statIconStyle, background: 'var(--accent-soft)', color: 'var(--accent)' }}>
                      <Sparkles size={16} />
                    </span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontFamily: 'var(--font-ui)', fontWeight: 600, fontSize: 14, color: 'var(--text-primary)' }}>
                        {dm.fillAllAiRow(aiCount)}
                      </div>
                      <div style={{ fontFamily: 'var(--font-ui)', fontSize: 11.5, color: 'var(--text-secondary)' }}>{dm.fillAllAiNote}</div>
                    </div>
                    <span style={{ fontFamily: 'var(--font-content)', fontWeight: 700, fontSize: 18, color: 'var(--accent)' }}>{aiCount}</span>
                  </div>
                )}
              </div>

              {total > 0 && (
                <div style={{ display: 'flex', height: 7, borderRadius: 'var(--radius-pill)', overflow: 'hidden', background: 'var(--bg-secondary)' }}>
                  <span style={{ width: `${dictPct}%`, background: 'var(--success)' }} />
                  <span style={{ width: `${100 - dictPct}%`, background: 'var(--accent)' }} />
                </div>
              )}

              {/* The cost half of the disclosure, and it is stated whether or
                  not the budget is short: the sheet already says how many
                  cards will go through AI, and a reader cannot tell whether
                  that fits without knowing what is left. Opening this sheet
                  spends none of it — the server reads the counter, never
                  increments it.

                  When the counter could not be read, the sheet says so rather
                  than printing the fail-open fallback. An unknown allowance
                  shown as a full one would read as "no limit here", which is
                  the one thing it must not imply. */}
              {!preflight.ai_budget_known ? (
                <div style={{ fontFamily: 'var(--font-ui)', fontSize: 12, color: 'var(--warning)' }}>
                  {dm.fillAllAllowanceUnknown}
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <div style={{
                    fontFamily: 'var(--font-ui)', fontSize: 12,
                    color: preflight.ai_budget_sufficient ? 'var(--text-secondary)' : 'var(--warning)',
                  }}>
                    {dm.fillAllAllowance(preflight.ai_remaining_budget, preflight.ai_daily_limit)}
                  </div>
                  {!preflight.ai_budget_sufficient && aiCount > 0 && (
                    <div style={{ fontFamily: 'var(--font-ui)', fontSize: 12, color: 'var(--warning)' }}>
                      {dm.fillAllBudgetWarning(preflight.ai_remaining_budget)}
                    </div>
                  )}
                </div>
              )}

              <div style={{ display: 'flex', flexDirection: 'column', gap: 9, marginTop: 2 }}>
                <button onClick={onConfirm} disabled={starting} style={confirmBtnStyle(starting)}>
                  {dm.fillAllConfirmBtn(preflight.total_unfilled)}
                </button>
                <button onClick={onCancel} disabled={starting} style={cancelBtnStyle}>
                  {tr.deckDetail.cancelLabel}
                </button>
              </div>
            </>
          ) : null}
        </div>
      </div>
    </div>
  )
}

const statRowStyle = {
  display: 'flex', alignItems: 'center', gap: 11, background: 'var(--bg-secondary)',
  border: '1px solid var(--border)', borderRadius: 12, padding: '11px 13px',
} as const
const statIconStyle = {
  width: 32, height: 32, borderRadius: 9, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
} as const
const cancelBtnStyle = {
  width: '100%', background: 'transparent', color: 'var(--text-secondary)', border: 'none',
  borderRadius: 'var(--radius-pill)', padding: '10px 18px', fontFamily: 'var(--font-ui)', fontWeight: 600,
  fontSize: 14, cursor: 'pointer',
} as const
function confirmBtnStyle(disabled: boolean) {
  return {
    width: '100%', background: 'var(--accent-fill)', color: 'var(--text-on-accent)', border: 'none',
    borderRadius: 'var(--radius-pill)', padding: '13px 18px', fontFamily: 'var(--font-ui)', fontWeight: 600,
    fontSize: 14.5, cursor: disabled ? 'default' : 'pointer', opacity: disabled ? 0.7 : 1,
    boxShadow: 'var(--shadow-card)',
  } as const
}
